import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { CardTheme } from "../../../models/cardThemes";
import { ProfileImage } from "../../../models/profileImages";
import { SplashScreen } from "../../../models/splashScreens";
import { User as UserModel } from "../../../models/user";
import * as UserApi from "../../../network/user_api";
import styles from "../StorePage.module.scss";
import { GiTwoCoins } from "react-icons/gi";
import * as CollectiblesApi from "../../../network/collectibles_api";

interface CollectiblesProps {
  user: UserModel;
  onProfileSaved: (user: UserModel) => void;
  className?: string;
  collectibles: SplashScreen[] | ProfileImage[] | CardTheme[];
  collectibleType: "splashScreens" | "profileImages" | "cardThemes";
}

const Collectibles = ({
  user,
  onProfileSaved,
  className,
  collectibles,
  collectibleType,
}: CollectiblesProps) => {
  const { t, i18n } = useTranslation();
  const [selectedCollectible, setSelectedCollectible] = useState<
    SplashScreen | ProfileImage | CardTheme | null
  >(null);
  const [showPopup, setShowPopup] = useState(false);

  const handleCollectibleClick = (
    collectible: SplashScreen | ProfileImage | CardTheme
  ) => {
    setSelectedCollectible(collectible);
    setShowPopup(true);
  };

  const handlePurchase = async () => {
    if (!selectedCollectible || !user) return;

    if (user.coin < selectedCollectible.value) {
      alert(t("store.notEnoughCoins"));
      setShowPopup(false);
      return;
    }

    try {
      // Update user's coins
      const updatedUser = {
        ...user,
        xp: 0,
        coin: user.coin - selectedCollectible.value,
      };
      const userResponse = await UserApi.updateUser(updatedUser);

      // Update the collectible list and remove the purchased item
      let updatedCollectibles = [...collectibles];
      updatedCollectibles = updatedCollectibles.filter(
        (item) => item.fileName !== selectedCollectible.fileName
      );

      // Make the API call to store the purchase in the backend
      if (collectibleType === "profileImages") {
        await CollectiblesApi.createProfileImage({
          fileName: selectedCollectible.fileName,
          englishTitle: selectedCollectible.englishTitle,
          hungarianTitle: selectedCollectible.hungarianTitle,
          type: selectedCollectible.type,
          value: selectedCollectible.value,
        });
      } else if (collectibleType === "splashScreens") {
        await CollectiblesApi.createSplashScreen({
          fileName: selectedCollectible.fileName,
          englishTitle: selectedCollectible.englishTitle,
          hungarianTitle: selectedCollectible.hungarianTitle,
          type: selectedCollectible.type,
          value: selectedCollectible.value,
        });
      } else {
        await CollectiblesApi.createCardTheme({
          fileName: selectedCollectible.fileName,
          englishTitle: selectedCollectible.englishTitle,
          hungarianTitle: selectedCollectible.hungarianTitle,
          type: selectedCollectible.type,
          value: selectedCollectible.value,
        });
      }

      // Update the user and collectibles
      onProfileSaved(userResponse);

      // Update the UI by removing the purchased item from the list
      collectibles.splice(
        collectibles.findIndex(
          (item) => item.fileName === selectedCollectible.fileName
        ),
        1
      );

      setShowPopup(false);
    } catch (error) {
      console.error(error);
      alert(t("error.purchaseFailed"));
    }
  };

  return (
    <>
      {collectibles.length === 0 && (
        <p className={styles.noItemsMessage}>{t("store.noItems")}</p>
      )}
  
      <div className={styles.collectiblesGrid}>
        {collectibles.map((collectible, index) => (
          <div
            key={index}
            className={styles.collectibleItem}
            onClick={() => handleCollectibleClick(collectible)}
          >
            <img
              src={`${process.env.REACT_APP_API_URL}/images/collectibles/${collectibleType}/${collectible.fileName}`}
              alt={collectible.fileName}
              className={styles.collectibleImage}
            />
            <h3 className={styles.collectibleTitle}>
              {i18n.language === "hu"
                ? collectible.hungarianTitle
                : collectible.englishTitle}
            </h3>
            <p className={styles.collectibleType}>{collectible.type}</p>
            <p className={styles.collectibleValue}>
              <GiTwoCoins /> {collectible.value}
            </p>
          </div>
        ))}
      </div>
  
      {showPopup && selectedCollectible && (
        <div className={styles.popupOverlay}>
          <div className={styles.popupContent}>
            <div className={styles.popupImageContainer}>
              <img
                src={`${process.env.REACT_APP_API_URL}/images/collectibles/${collectibleType}/${selectedCollectible.fileName}`}
                alt={selectedCollectible.fileName}
                className={styles.popupImage}
              />
            </div>
            <div className={styles.popupDetails}>
              <h3>
                {t("store.purchasePrompt")}{" "}
                {i18n.language === "hu"
                  ? selectedCollectible.hungarianTitle
                  : selectedCollectible.englishTitle}
                ?
              </h3>
              <p>
                {t("store.cost")}: <GiTwoCoins /> {selectedCollectible.value}
              </p>
              {user.coin < selectedCollectible.value ? (
                <p className={styles.errorMessage}>{t("store.notEnoughCoins")}</p>
              ) : (
                <p>
                  {t("store.currentBalance")}: <GiTwoCoins /> {user.coin}
                </p>
              )}
              <div className={styles.popupButtons}>
                <button onClick={() => setShowPopup(false)}>
                  {t("common.cancel")}
                </button>
                {user.coin >= selectedCollectible.value && (
                  <button onClick={handlePurchase}>{t("store.buy")}</button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );  
};

export default Collectibles;
